import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Hover grace period, in milliseconds, before an open panel closes.
   *
   * Sophie Marea, 2026-09-14. Measured on the draft: a menu item's box ends at
   * y=81 and the submenu panel's hit area begins at y=92, so there is an 11px
   * band belonging to `.header__columns` that the pointer MUST cross to reach
   * the panel. Crossing it fired `pointerleave` on the item, and `deactivate`
   * closed the panel with no delay at all - its existing guards only cover
   * focus movement (`event.type === 'blur'`, `document.activeElement`), never
   * a pointer. The panel's `clip-path` is also transitioned, so while it is
   * opening it has no hit area for the pointer to land in.
   *
   * 220ms is long enough to cross that band at a normal, and even a slow,
   * hand speed, and short enough that leaving the header still feels immediate.
   * Switching between top-level items stays instant because `activate` cancels
   * any pending close before it runs.
   */
  #closeDelay = 220;

  /**
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #closeTimer = null;

  /** Whether the pointer corridor is currently being tracked. */
  #trackingPointer = false;

  /**
   * Close the open panel after the grace period, unless something cancels it
   * first. Repeated calls restart the clock rather than stacking timers.
   */
  #scheduleDeactivate = () => {
    this.#cancelScheduledDeactivate();
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      this.#deactivate();
    }, this.#closeDelay);
  };

  /** Abandon a pending close. */
  #cancelScheduledDeactivate = () => {
    if (this.#closeTimer == null) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  };

  /**
   * While a panel is open, treat the whole header-and-panel corridor as "still
   * in the menu", so the dead band between the two, and any diagonal path
   * across it, no longer closes the panel. Pointer-device only: on touch there
   * is no hover to be tolerant of.
   */
  #startPointerTracking = () => {
    if (this.#trackingPointer) return;
    if (!window.matchMedia?.('(hover: hover)').matches) return;
    this.#trackingPointer = true;
    document.addEventListener('pointermove', this.#pointerCorridorListener, { passive: true });
  };

  #stopPointerTracking = () => {
    if (!this.#trackingPointer) return;
    this.#trackingPointer = false;
    document.removeEventListener('pointermove', this.#pointerCorridorListener);
  };

  /**
   * @param {PointerEvent} event
   */
  #pointerCorridorListener = (event) => {
    if (!this.#state.activeItem) return;

    const header = this.headerComponent ?? this;
    const headerRect = header.getBoundingClientRect();
    const panel = findSubmenu(this.#state.activeItem);
    const panelRect = panel?.getBoundingClientRect();

    const top = headerRect.top;
    const bottom = Math.max(headerRect.bottom, panelRect?.bottom ?? headerRect.bottom);
    const left = Math.min(headerRect.left, panelRect?.left ?? headerRect.left);
    const right = Math.max(headerRect.right, panelRect?.right ?? headerRect.right);

    const inside =
      event.clientX >= left && event.clientX <= right && event.clientY >= top && event.clientY <= bottom;

    if (inside) {
      this.#cancelScheduledDeactivate();
      return;
    }

    // Outside the corridor: start the clock once and let it run down. Starting
    // it afresh on every move would let a pointer that is slowly leaving keep
    // deferring its own close indefinitely.
    if (this.#closeTimer == null) this.#scheduleDeactivate();
  };

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    document.addEventListener('keydown', this.#escapeListener);
    this.addEventListener('pointerleave', this.#clearEscapeDismissed);
    this.addEventListener('focusout', this.#focusoutListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    document.removeEventListener('keydown', this.#escapeListener);
    this.removeEventListener('pointerleave', this.#clearEscapeDismissed);
    this.removeEventListener('focusout', this.#focusoutListener);
    this.#cancelScheduledDeactivate();
    this.#stopPointerTracking();
    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  /**
   * The menu item most recently dismissed with Escape, if any.
   * @type {HTMLElement | null}
   */
  #escapeDismissedItem = null;

  /**
   * Close an open mega menu when Escape is pressed.
   *
   * Sophie Marea, 2026-09-10. WCAG 2.1 SC 1.4.13 asks that content revealed on
   * hover or focus can be dismissed without moving the pointer or the focus.
   * Savor ships no Escape handling for the header menu, so a panel opened by
   * hovering or by tabbing could only be closed by moving away from it.
   *
   * Deliberately conservative, so nothing else in the header is disturbed:
   *   - does nothing unless this menu actually has a panel open, which leaves
   *     the cart drawer, search and quick-add free to handle their own Escape;
   *   - stands down entirely while a modal dialog is open, for the same reason;
   *   - calls neither preventDefault nor stopPropagation, so no other listener
   *     is starved of the event.
   *
   * Rollback: assets/header-menu.js md5 d00fa01935affc695b1ea4999f4a829c
   * @param {KeyboardEvent} event
   */
  #escapeListener = (event) => {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;

    const item = this.#state.activeItem;
    if (!item) return;

    // A modal owns Escape for as long as it is open.
    if (document.querySelector('dialog[open], [aria-modal="true"]')) return;

    const focusWasInsideMenu = this.contains(document.activeElement);

    // Remember the dismissal so a stationary pointer, or the focus we are about
    // to return to the trigger, cannot immediately re-open the same panel.
    this.#escapeDismissedItem = item;
    this.#deactivate(item, true);

    // Return focus to the trigger only when focus was already inside the menu.
    // A panel opened by hover must not steal focus from wherever the reader is.
    if (focusWasInsideMenu && typeof item.focus === 'function') item.focus();
  };

  /**
   * Any pointer or focus move away from the menu ends the Escape dismissal, so
   * the next hover or Tab opens the panel normally again.
   */
  #clearEscapeDismissed = () => {
    this.#escapeDismissedItem = null;
  };

  /**
   * @param {FocusEvent} event
   */
  #focusoutListener = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#clearEscapeDismissed();
  };

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // Moving onto any menu item calls off a pending close at once, so moving
    // sideways between top-level items stays as responsive as it was.
    this.#cancelScheduledDeactivate();

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    // While a panel is dismissed with Escape, ignore re-activation of that same
    // item, so a stationary pointer or the focus we returned to the trigger
    // cannot spring it straight back open.
    if (item === this.#escapeDismissedItem) return;

    // Moving to a different item ends the dismissal. Without this, tabbing or
    // hovering from the dismissed item to a sibling and back would leave the
    // first panel permanently unopenable until the pointer left the menu.
    this.#escapeDismissedItem = null;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');

    if (finalHeight > 0) this.#startPointerTracking();
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    // The pointer-side equivalent of the three guards above. The submenu panel
    // is a DOM descendant of the menu item, but it is positioned against
    // `.header__row`, so a pointer travelling towards it - especially
    // diagonally, towards a link that is not directly below the trigger -
    // leaves the item's box for a few pixels. Anything still inside this
    // `<header-menu>` is movement within the menu, not out of it.
    const isMovingWithinComponent = event.relatedTarget instanceof Node && this.contains(event.relatedTarget);

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu || isMovingWithinComponent) {
      this.#cancelScheduledDeactivate();
      return;
    }

    // Everything else gets the grace period rather than an immediate close.
    // If the pointer is genuinely on its way out, the timer runs down and the
    // panel closes; if it is crossing the gap towards the panel, the corridor
    // listener or the next `activate` calls the close off.
    this.#scheduleDeactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {boolean} [force] - Bypass the hover guard (used by the Escape handler).
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    this.#cancelScheduledDeactivate();

    if (!item || item != this.#state.activeItem) {
      this.#stopPointerTracking();
      return;
    }

    // Don't deactivate if the overflow menu or overflow list is still being hovered.
    // Escape passes force, because dismissing without moving the pointer is the
    // entire point of it.
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.#stopPointerTracking();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
